import {
  LayoutDashboard,
  FileCheck2,
  CalendarClock,
  Repeat,
  TrendingUp,
  Truck,
  Boxes,
  Gauge,
  ShieldCheck,
  Recycle,
  BadgeAlert,
  FileText,
  Users,
  BarChart3,
  Ship,
  Settings,
  ClipboardCheck,
  ClipboardList,
  ClipboardEdit,
  FileBarChart,
  History,
  ScrollText,
  type LucideIcon,
} from "lucide-react";

export interface NavItem {
  key: string; // matches categories.link_key and the React route path
  label: string;
  icon: LucideIcon;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

/** Ported 1:1 from the approved prototype's sidebar (§3.1) — icons, labels,
 * and `data-view` keys (here: route + category link_key) are unchanged.
 * Visibility is now driven by the authenticated user's real `categories`
 * (server-resolved RBAC), not a client-side role switch statement. */
export const NAV_GROUPS: NavGroup[] = [
  { label: "Overview", items: [{ key: "dashboard", label: "Dashboard", icon: LayoutDashboard }] },
  {
    label: "Orders & Delivery",
    items: [
      { key: "po-result", label: "PO Confirmation", icon: FileCheck2 },
      { key: "po-proposals", label: "Supplier PO Proposals", icon: ClipboardCheck },
      { key: "sla-result", label: "SLA Confirmation", icon: CalendarClock },
      { key: "sla-proposals", label: "Supplier SLA Proposals", icon: ClipboardList },
      { key: "firmsla-result", label: "Firm SLA Change", icon: Repeat },
      { key: "firmsla-proposals", label: "Firm SLA Change Proposals", icon: ClipboardEdit },
      { key: "dpo-forecast", label: "DPO Forecast", icon: TrendingUp },
      { key: "asn", label: "ASN Creation", icon: Truck },
      { key: "wip", label: "Production / WIP", icon: Boxes },
      { key: "vcc", label: "Vendor Capacity", icon: Gauge },
    ],
  },
  {
    label: "Reports & Audit",
    items: [
      { key: "po-confirmed-report", label: "Confirmed PO Report", icon: FileBarChart },
      { key: "po-proposal-history", label: "PO Proposal History", icon: History },
      { key: "po-audit-log", label: "PO Confirm Audit Log", icon: ScrollText },
      { key: "sla-confirmed-report", label: "Confirmed SLA Report", icon: FileBarChart },
      { key: "sla-proposal-history", label: "SLA Proposal History", icon: History },
      { key: "sla-audit-log", label: "SLA Confirm Audit Log", icon: ScrollText },
    ],
  },
  {
    label: "Compliance",
    items: [
      { key: "rsds", label: "RSDS Declaration", icon: ShieldCheck },
      { key: "crppc", label: "CRPPC Declaration", icon: Recycle },
      { key: "quality", label: "Quality / NCR", icon: BadgeAlert },
      { key: "etariff", label: "E-Tariff Exclusions", icon: FileText },
    ],
  },
  {
    label: "Administration",
    items: [
      { key: "useradmin", label: "User Management", icon: Users },
      { key: "usage", label: "Usage Analytics", icon: BarChart3 },
      { key: "freight", label: "Freight Partners", icon: Ship },
      { key: "config", label: "Configuration", icon: Settings },
    ],
  },
];

export const PAGE_TITLES: Record<string, [string, string]> = {
  dashboard: ["Dashboard", "Your role-aware home base"],
  "po-result": ["PO Confirmation", "GspPO — real-time PO Confirmation Result"],
  "po-split": ["PO Line Split", "Propose quantity/date changes"],
  "po-proposals": ["Supplier PO Proposals", "Approve or reject supplier proposals"],
  "po-confirmed-report": ["Confirmed PO Report", "Actually-confirmed/approved PO line values (read-only)"],
  "po-proposal-history": ["Supplier PO Proposal History Report", "All supplier proposals, every status (read-only)"],
  "po-audit-log": ["PO Confirm Audit Log", "Every PO Confirmation state transition — SBD-only"],
  "sla-result": ["SLA Confirmation", "GspSLAConfirm — real-time SLA Confirmation Result"],
  "sla-split": ["SLA Line Split", "Propose quantity/date changes"],
  "sla-proposals": ["Supplier SLA Proposals", "Approve or reject SLA proposals"],
  "firmsla-result": ["Firm SLA Change Request", "Change already-confirmed SLA lines"],
  "firmsla-proposals": ["Firm SLA Change Proposals", "Approve or reject Firm SLA Change proposals"],
  "sla-confirmed-report": ["Confirmed SLA Report", "Actually-confirmed/approved SLA line values (read-only)"],
  "sla-proposal-history": ["Supplier SLA Proposal History Report", "All SLA proposals, every status (read-only)"],
  "sla-audit-log": ["SLA Confirm Audit Log", "Every SLA Confirmation state transition — SBD-only"],
  "dpo-forecast": ["DPO Forecast", "18-month rolling demand forecast (read-only)"],
  asn: ["ASN Creation", "eFulfill — single-page autosaving form"],
  wip: ["Production Order / WIP", "GspPrdO — container tracking"],
  vcc: ["Vendor Capacity Check", "GSPVCC — 18 rolling months"],
  rsds: ["RSDS Declaration", "Restricted substances compliance"],
  crppc: ["CRPPC Declaration", "Recycled-content / packaging declaration"],
  quality: ["Quality / Non-Conformance", "eQuality — supplier NCR response"],
  etariff: ["E-Tariff Exclusion Requests", "Section 232/301 exclusion wizard"],
  useradmin: ["User & Account Management", "Roles, vendors, plants — one console"],
  usage: ["Usage Analytics", "Admin usage report"],
  freight: ["Freight Partner Management", "GSMFP — carrier assignment"],
  config: ["System Configuration", "GspConfiguration — fields & mappings"],
};
